/**
 * Reportes → Productividad por turnos (`/reportes/productividad-turnos`).
 *
 * Puerto de `Reportes::productividad_por_shift` + `Reportes_model::
 * Productividad_model::get_productividad_por_shift` + `views/menu/reportes/
 * productividad_por_shift.php` (y su `productividad_pdf.php`).
 *
 * La pantalla del panel anterior pedía **fecha de inicio, fecha de fin**, el **turno**
 * (`shift_cat`) y el **tipo de reporte** (completo o resumido) y pintaba una tabla con
 * Fecha, Turno, Producto, Cantidad, Monto y Volumen, con filtros por tipo de documento
 * (la serie del comprobante) y exports a Excel y PDF. El rango estaba limitado a **90
 * días**.
 *
 * Reglas conservadas:
 * - Las ventas de cada turno se leen del JSON `turno.transactions` (`JSON_TABLE`) y el
 *   turno se deduce de la **hora de llegada** contra la ventana de `shift_cat` (el turno
 *   de noche cruza la medianoche).
 * - Se añade el bloque de **MARKET** (`market_document_response` con `state_type_id =
 *   '05'`) como un producto más, con la ventana del turno: la madrugada del día
 *   siguiente pertenece al turno de noche del día anterior.
 * - El tope de **90 días** del rango se mantiene (el original lo rechazaba por encima).
 *
 * Diferencias anotadas:
 * - Se añaden las tarjetas del periodo, la comparación **por turno** (con su venta media
 *   por día trabajado), el reparto por producto y por serie, y el día a día agrupado por
 *   turno (el original tenía un único listado plano).
 * - El CSV y el PDF los sirve la API (antes los armaba el navegador).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosProductividad,
  useProductividadTurnos,
  type FiltrosProductividad,
} from '../reportes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: los últimos 30 días. */
function rangoPorDefecto(): FiltrosProductividad {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getTime() - 30 * 24 * 60 * 60 * 1000)),
    hasta: fechaLocal(hoy),
    turno: '',
    producto: '',
    serie: '',
  };
}

export function ReporteProductividadTurnosPage() {
  useDocumentTitle('Reportes · Productividad por turnos');

  const [filtros, setFiltros] = useState<FiltrosProductividad>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosProductividad();
  const consulta = useProductividadTurnos(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const dias = datos?.dias ?? [];
  const porTurno = datos?.por_turno ?? [];
  const paginacion = usePaginacion(porTurno, { clave: filtros });
  const porProducto = datos?.por_producto ?? [];
  const porSerie = datos?.por_serie ?? [];

  const turnos = catalogos.data?.turnos ?? [];
  const productos = catalogos.data?.productos ?? [];
  const series = catalogos.data?.series ?? [];
  const maximoDias = catalogos.data?.maximo_dias ?? 90;
  const meses = catalogos.data?.meses ?? [];

  const cambiar = <C extends keyof FiltrosProductividad>(
    campo: C,
    valor: FiltrosProductividad[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarProductividad(filtros), {
        nombre: `productividad-turnos-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfProductividad(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const diasEnRango =
    Math.round(
      (new Date(`${filtros.hasta}T12:00:00`).getTime() -
        new Date(`${filtros.desde}T12:00:00`).getTime()) /
        86400000,
    ) + 1;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Productividad por turnos</h1>
          <p className="mt-1 text-sm text-ink-500">
            La venta de cada turno (por producto y comprobante), con su comparación por día.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">
                Hasta{' '}
                <span className="font-normal text-ink-400">({formatNumber(diasEnRango)} días)</span>
              </span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Turno</span>
              <Select
                value={filtros.turno}
                onChange={({ target }) => cambiar('turno', target.value)}
                className={CONTROL}
                aria-label="Turno"
              >
                <option value="">TODOS</option>
                {turnos.map((turno) => (
                  <option key={turno.id} value={String(turno.id)}>
                    {turno.turno} ({turno.inicio} - {turno.fin})
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.producto}
                onChange={({ target }) => cambiar('producto', target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                <option value="">TODOS</option>
                {productos.map((producto) => (
                  <option key={producto.producto} value={producto.producto ?? ''}>
                    {producto.producto} ({formatNumber(producto.ventas)})
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
            <span className="mr-1 text-xs font-semibold text-ink-500">Comprobantes</span>
            {series.map((serie) => {
              const marcada = filtros.serie === serie.serie;

              return (
                <button
                  key={serie.serie}
                  type="button"
                  aria-pressed={marcada}
                  onClick={() => cambiar('serie', marcada ? '' : serie.serie ?? '')}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                    marcada
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                  )}
                  title={`${formatNumber(serie.ventas)} venta(s)`}
                >
                  {serie.serie}
                </button>
              );
            })}
            {filtros.serie ? (
              <button
                type="button"
                onClick={() => cambiar('serie', '')}
                className="ml-1 text-xs font-semibold text-ink-500 hover:text-brand-700 hover:underline"
              >
                Limpiar
              </button>
            ) : null}
            <span className="ml-auto text-xs text-ink-500">
              el rango máximo es de {formatNumber(maximoDias)} días
            </span>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 text-xs font-semibold text-ink-500">Meses con turnos</span>
              {meses.slice(0, 12).map((mes) => {
                const anio = Number(mes.periodo.slice(0, 4));
                const numero = Number(mes.periodo.slice(5, 7));
                const rango = {
                  desde: `${mes.periodo}-01`,
                  hasta: fechaLocal(new Date(anio, numero, 0)),
                };
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.dias)} día(s) con ventas`}
                  >
                    {mes.periodo}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {[
          {
            etiqueta: 'Venta del periodo',
            valor: formatCurrency(resumen?.monto ?? 0),
            detalle: `${formatNumber(resumen?.ventas ?? 0)} venta(s)`,
          },
          {
            etiqueta: 'Volumen',
            valor: formatNumber(resumen?.volumen ?? 0),
            detalle: `${formatNumber(resumen?.dias ?? 0)} día(s)`,
          },
          {
            etiqueta: 'Turnos',
            valor: formatNumber(resumen?.turnos ?? 0),
            detalle: `${formatNumber(porProducto.length)} producto(s)`,
          },
          {
            etiqueta: 'Promedio por turno',
            valor: formatCurrency(resumen?.promedio_turno ?? 0),
            detalle: 'del periodo',
          },
          {
            etiqueta: 'MARKET',
            valor: formatCurrency(resumen?.market_monto ?? 0),
            detalle: `${formatNumber(resumen?.market_ventas ?? 0)} venta(s)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-sm font-semibold text-ink-800">Productividad por turno</h2>

          {consulta.isLoading ? <Skeleton className="h-32 w-full" /> : null}

          {!consulta.isLoading ? (
            <div className="overflow-hidden rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Turno</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Días</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Ventas</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Monto</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Volumen</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Promedio / día</th>
                  </tr>
                </thead>
                <tbody>
                  {porTurno.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={6} className="px-2 py-8 text-center text-sm text-ink-500">
                        No hay ventas de turno que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr key={fila.turno} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="px-2 py-2 font-semibold text-ink-900">{fila.turno}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.dias)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.ventas)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.monto)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.volumen)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatCurrency(fila.promedio_dia)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {porTurno.length > 0 ? (
                  <tfoot className="bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2">TOTALES</td>
                      <td className="px-2 py-2 text-right" />
                      <td className="px-2 py-2 text-right">
                        {formatNumber(resumen?.ventas ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">{formatCurrency(resumen?.monto ?? 0)}</td>
                      <td className="px-2 py-2 text-right">
                        {formatNumber(resumen?.volumen ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen?.promedio_turno ?? 0)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}

          {!consulta.isLoading ? <Paginador {...paginacion} etiqueta="turnos" /> : null}
        </CardContent>
      </Card>

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!consulta.isLoading
        ? dias.map((dia) => (
            <Card key={dia.fecha}>
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-ink-800">
                    {formatDateValue(dia.fecha)}
                  </h2>
                  <span className="text-xs text-ink-500">
                    {formatCurrency(dia.totales.monto)} · {formatNumber(dia.totales.ventas)} venta(s)
                    · {formatNumber(dia.totales.volumen)} gal
                    {dia.mejor_turno ? ` · mejor turno: ${dia.mejor_turno}` : ''}
                  </span>
                </div>

                <div className="grid gap-3 xl:grid-cols-3">
                  {dia.turnos.map((turno) => (
                    <div key={turno.turno} className="overflow-hidden rounded-card border border-ink-100">
                      <div className="flex items-center justify-between gap-2 bg-ink-50/60 px-2 py-1.5">
                        <span className="text-xs font-semibold text-ink-800">{turno.turno}</span>
                        <span className="text-xs text-ink-600">
                          {formatCurrency(turno.totales.monto)}
                        </span>
                      </div>
                      <table className="w-full table-fixed border-collapse text-[12px]">
                        <thead>
                          <tr className="text-left text-[10px] font-semibold tracking-wide text-ink-500 uppercase">
                            <th className="px-1.5 py-1">Producto</th>
                            <th className="w-[3.25rem] px-1.5 py-1 text-right">Ventas</th>
                            <th className="w-[4.5rem] px-1.5 py-1 text-right">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {turno.productos.map((producto, indice) => (
                            <tr
                              key={`${producto.producto}-${producto.serie}-${indice}`}
                              className="border-t border-ink-100"
                            >
                              <td className="px-1.5 py-1">
                                <p className="truncate text-ink-800" title={producto.producto ?? ''}>
                                  {producto.producto ?? '—'}
                                </p>
                                <p className="truncate text-[10px] text-ink-500">
                                  {producto.serie}
                                  {producto.tipo === 'MARKET' ? (
                                    <Badge variant="brand" className="ml-1 px-1 py-0 text-[9px]">
                                      MARKET
                                    </Badge>
                                  ) : null}
                                </p>
                              </td>
                              <td className="px-1.5 py-1 text-right text-ink-700">
                                {formatNumber(producto.ventas)}
                              </td>
                              <td className="px-1.5 py-1 text-right font-semibold text-ink-900">
                                {formatCurrency(producto.monto)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="border-t border-ink-100 bg-white px-2 py-1 text-[11px] text-ink-500">
                        {formatNumber(turno.totales.volumen)} gal · precio medio{' '}
                        {formatCurrency(turno.precio_medio)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por producto</h2>

            <div className="overflow-hidden rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Producto</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Ventas</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {porProducto.map((fila) => (
                    <tr key={fila.producto} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.producto ?? '—'}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.ventas)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por comprobante</h2>

            <div className="overflow-hidden rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Serie</th>
                    <th className="w-[5.5rem] px-2 py-2 text-right">Ventas</th>
                    <th className="w-[7rem] px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {porSerie.map((fila) => (
                    <tr key={fila.serie} className="border-t border-ink-100">
                      <td className="px-2 py-2 font-semibold text-ink-900">{fila.serie ?? '—'}</td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.ventas)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.monto)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Las ventas de cada turno salen de las transacciones del turno (el JSON del surtidor) y el
          turno se deduce de la <strong>hora de llegada</strong> contra la ventana configurada en
          cada turno (el de noche cruza la medianoche). El bloque <strong>MARKET</strong> se cuenta
          aparte, con la misma ventana: las ventas de la madrugada pertenecen al turno de noche del
          día anterior. El rango máximo es de 90 días, como en el panel anterior.
        </span>
      </p>
    </div>
  );
}
